import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  FolderTree,
  Info,
  History,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utils/cn'
import {
  getStatusBadgeHoverClassName,
  getStatusLabel,
} from '@/utils/statusConfig'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { usePlanVersions } from '@/hooks/usePlanDetail'
import { PlanInformationDrawer } from './PlanInformationDrawer'
import { SharePlanModal } from './SharePlanModal'
import { SubmitForReviewModal } from './SubmitForReviewModal'
import { usePlanSubmissions } from '@/hooks/usePlanSubmissions'
import { useUnsubmitPlan } from '@/hooks/useUnsubmitPlan'
import { PlanActionsMenu } from './PlanActionsMenu'
import { SaveVersionModal } from './SaveVersionModal'
import { ManageVersionsDrawer } from './ManageVersionsDrawer'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useDeletePlan } from '@/hooks/useAssortmentPlans'
import type { ApiPlanDetailResponse, ApiPlanStatus } from '@/types'

interface PlanDetailHeaderProps {
  planId: string
  name: string
  status: ApiPlanStatus
  hierarchyBreadcrumb: string
  planMetadata?: ApiPlanDetailResponse
}

export function PlanDetailHeader({
  planId,
  name,
  status,
  hierarchyBreadcrumb,
  planMetadata,
}: PlanDetailHeaderProps) {
  const navigate = useNavigate()
  const deleteMutation = useDeletePlan()

  const { versionId, setCurrentVersion } = useCurrentVersionId(planId)
  const { data: versions } = usePlanVersions(planId)
  const currentVersion = versions?.find(v => v.id === versionId)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerDefaultTab, setDrawerDefaultTab] = useState<'info' | 'history'>(
    'info'
  )
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [submitModalOpen, setSubmitModalOpen] = useState(false)
  const [saveVersionModalOpen, setSaveVersionModalOpen] = useState(false)
  const [manageVersionsDrawerOpen, setManageVersionsDrawerOpen] =
    useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [unsubmitDialogOpen, setUnsubmitDialogOpen] = useState(false)

  // Fetch submissions to find the pending one (for unsubmit)
  const { data: submissions } = usePlanSubmissions(planId)
  const unsubmitMutation = useUnsubmitPlan(planId)
  const pendingSubmission = submissions?.find(s => s.status === 'PENDING')

  const handleInfoClick = () => {
    setDrawerDefaultTab('info')
    setDrawerOpen(true)
  }

  const handleHistoryClick = () => {
    setDrawerDefaultTab('history')
    setDrawerOpen(true)
  }

  const handleConfirmUnsubmit = () => {
    if (pendingSubmission) {
      unsubmitMutation.mutate(pendingSubmission.id, {
        onSuccess: () => setUnsubmitDialogOpen(false),
        onSettled: () => setUnsubmitDialogOpen(false),
      })
    }
  }

  const handleConfirmDelete = () => {
    deleteMutation.mutate(planId, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        navigate('/')
      },
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumbs + Version Selector */}
      <nav className="flex items-center justify-between gap-2 text-sm text-muted-foreground overflow-hidden min-w-0">
        <div className="flex items-center gap-1 overflow-hidden min-w-0">
          <Link
            to="/"
            className="hover:text-foreground hover:underline cursor-pointer shrink-0"
          >
            Assortment Plans
          </Link>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span className="text-foreground font-medium truncate min-w-0">
            {name}
          </span>
        </div>

        {currentVersion && versions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 shrink-0 cursor-pointer focus:outline-none">
                <span className="text-sm text-muted-foreground">Viewing:</span>
                <Badge className="bg-accent border border-accent-border text-primary hover:bg-accent font-medium py-1 gap-1">
                  {currentVersion.alias}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Badge>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {versions.map(version => (
                <DropdownMenuItem
                  key={version.id}
                  className={cn(
                    'cursor-pointer',
                    version.id === versionId && 'bg-accent font-medium'
                  )}
                  onClick={() => setCurrentVersion(version.id)}
                >
                  {version.alias}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </nav>

      {/* Header row with title and actions */}
      <div className="flex flex-col gap-4 header:flex-row header:items-start header:justify-between">
        <div className="flex flex-col gap-3 min-w-0">
          {/* Title with status badge */}
          <div className="flex flex-wrap items-start gap-3 min-w-0 header:flex-nowrap header:items-center">
            <h1 className="text-xl font-semibold text-foreground wrap-break-word header:text-2xl header:truncate">
              {name}
            </h1>
            <Badge
              className={cn(
                'font-medium',
                'py-1',
                'shrink-0',
                getStatusBadgeHoverClassName(status)
              )}
            >
              {getStatusLabel(status)}
            </Badge>
          </div>

          {/* Plan type and hierarchy */}
          <div className="flex items-start gap-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-1.5">
              <FolderTree className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="wrap-break-word">{hierarchyBreadcrumb}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 shrink-0 header:justify-end">
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleInfoClick}
            title="Info"
          >
            <Info className="h-4 w-4" />
            <span className="hidden md:inline">Info</span>
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleHistoryClick}
            title="History"
          >
            <History className="h-4 w-4" />
            <span className="hidden md:inline">History</span>
          </Button>
          {status === 'SUBMITTED' && pendingSubmission ? (
            <Button
              variant="outline"
              className="gap-2 border-primary text-primary hover:bg-red-50"
              onClick={() => setUnsubmitDialogOpen(true)}
              title="Unsubmit"
            >
              <XCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Unsubmit</span>
            </Button>
          ) : (
            <Button
              className="gap-2"
              onClick={() => setSubmitModalOpen(true)}
              disabled={status !== 'DRAFT' && status !== 'SENT_BACK'}
              title="Submit for Review"
            >
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Submit for Review</span>
            </Button>
          )}
          <PlanActionsMenu
            onSharePlan={() => setShareModalOpen(true)}
            onSaveVersion={() => setSaveVersionModalOpen(true)}
            onManageVersions={() => setManageVersionsDrawerOpen(true)}
            onDeletePlan={() => setDeleteDialogOpen(true)}
          />
        </div>
      </div>

      <PlanInformationDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        defaultTab={drawerDefaultTab}
        planId={planId}
        planMetadata={planMetadata}
      />

      <SharePlanModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        planId={planId}
      />

      <SubmitForReviewModal
        open={submitModalOpen}
        onOpenChange={setSubmitModalOpen}
        planId={planId}
      />

      <SaveVersionModal
        open={saveVersionModalOpen}
        onOpenChange={setSaveVersionModalOpen}
        planId={planId}
      />

      <ManageVersionsDrawer
        open={manageVersionsDrawerOpen}
        onOpenChange={setManageVersionsDrawerOpen}
        planId={planId}
        status={status}
      />

      <ConfirmationDialog
        open={unsubmitDialogOpen}
        onOpenChange={setUnsubmitDialogOpen}
        title="Withdraw Submission"
        description="Are you sure you want to withdraw this submission? The plan will return to Draft status and you can make further changes before resubmitting."
        confirmText="Withdraw"
        cancelText="Cancel"
        onConfirm={handleConfirmUnsubmit}
        variant="destructive"
        isLoading={unsubmitMutation.isPending}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Plan"
        description="Are you sure you want to delete this plan? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
